import { useMemo, useState, useCallback, useEffect } from 'react'
import { View, Text, Pressable, Platform } from 'react-native'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import { format, startOfDay } from 'date-fns'
import { getGameResultsPageStyles } from '../../../appStyles/_gameResults.styles'
import { PageHeader, ErrorBoundary } from '../../../components/layout'
import { HEADER_FLAVORS } from '../../../components/layout/constants/pageHeader.constants'
import { GamesList, LeagueSelector } from '../../../components/gameResults'
import { CalendarStrip } from '../../../components/date'
import { useGameResults } from '../../../src/gameResults/hooks/useGameResults'
import {
  getWeekRangeForDate,
  formatDateToYYYYMMDD,
} from '../../../src/gameResults/helpers/dateRange.helper'
import { getResolvedScoreboardIanaTimeZone } from '../../../src/helpers/scoreboardIntlTimeZone.helper'
import { useTheme } from '../../../src/theme/useTheme'
import { GuestBanner } from '../../../src/shared/components/GuestBanner/GuestBanner'
import { useSkeletonLoading } from '../../../src/shared/hooks/useSkeletonLoading'
import {
  SECTION_FILTER_KEYS,
  SECTION_FILTER_OPTIONS,
  SCOREBOARD_VIEW_MODE_KEYS,
  SCOREBOARD_VIEW_MODE_OPTIONS,
  type SectionFilterKey,
  type ScoreboardViewModeKey,
} from '../../../src/gameResults/constants/gameResults.constants'
import { DataLoadErrorPanel } from '../../../src/shared/components/DataLoadErrorPanel/DataLoadErrorPanel'
import { SCOREBOARD_ERROR_COPY } from '../../../src/shared/constants/dataLoadError.constants'
import { AnimatedSegmentedControl } from '../../../components/ui/AnimatedSegmentedControl'
import { useAnalytics } from '../../../src/analytics/useAnalytics'
import { ANALYTICS_EVENTS } from '../../../src/analytics'

function getTodayYYYYMMDD(): string {
  return formatDateToYYYYMMDD(new Date())
}

function parseDateStr(s: string): Date {
  return startOfDay(new Date(s))
}

export default function GameResults() {
  const theme = useTheme()
  const { track } = useAnalytics()
  const styles = useMemo(() => getGameResultsPageStyles(theme), [theme])

  useEffect(() => {
    track(ANALYTICS_EVENTS.GAME_RESULTS_VIEWED)
  }, [track])

  const [viewMode, setViewMode] = useState<ScoreboardViewModeKey>(
    SCOREBOARD_VIEW_MODE_KEYS.DATE
  )
  const [sectionFilter, setSectionFilter] = useState<SectionFilterKey>(SECTION_FILTER_KEYS.ALL)
  const [selectedDate, setSelectedDate] = useState<string>(() => getTodayYYYYMMDD())
  const [leagueViewSettling, setLeagueViewSettling] = useState(false)

  const scoreboardIanaTimeZone = useMemo(() => getResolvedScoreboardIanaTimeZone(), [])

  const dateViewOptions = useMemo(() => {
    if (viewMode !== SCOREBOARD_VIEW_MODE_KEYS.DATE) return undefined
    return {
      selectedDate,
      range: getWeekRangeForDate(selectedDate),
      timezone: scoreboardIanaTimeZone,
    }
  }, [viewMode, selectedDate, scoreboardIanaTimeZone])

  const {
    filteredResults,
    isLoading,
    isLeagueViewPending,
    isPlaceholderData,
    isFetchingWithData,
    availableLeagues,
    selectedLeague,
    setSelectedLeague,
    isError,
    refetchResults,
  } = useGameResults(dateViewOptions, viewMode)

  const selectedDateAsDate = useMemo(() => parseDateStr(selectedDate), [selectedDate])
  const monthLabel = useMemo(() => format(selectedDateAsDate, 'MMMM yyyy'), [selectedDateAsDate])

  const handleSectionFilterPress = useCallback((key: SectionFilterKey) => {
    setSectionFilter(key)
    if (key === SECTION_FILTER_KEYS.LIVE) {
      setSelectedDate(getTodayYYYYMMDD())
    }
  }, [])

  const handleDateSelect = useCallback((date: Date) => {
    setSelectedDate(formatDateToYYYYMMDD(date))
  }, [])

  const handleViewModePress = useCallback((mode: ScoreboardViewModeKey) => {
    if (mode === SCOREBOARD_VIEW_MODE_KEYS.LEAGUE) {
      setLeagueViewSettling(true)
    } else {
      setLeagueViewSettling(false)
    }
    setViewMode(mode)
    if (mode === SCOREBOARD_VIEW_MODE_KEYS.DATE) {
      setSelectedDate(getTodayYYYYMMDD())
    }
  }, [])

  const isLeagueView = viewMode === SCOREBOARD_VIEW_MODE_KEYS.LEAGUE
  const showLeagueSkeleton =
    isLeagueView &&
    (isLeagueViewPending || leagueViewSettling || isPlaceholderData)
  const effectiveResults = showLeagueSkeleton ? [] : filteredResults
  const effectiveLoading = isLoading || isLeagueViewPending || (isLeagueView && leagueViewSettling)

  useEffect(() => {
    if (!isLeagueView || !leagueViewSettling) return
    const id = setTimeout(() => setLeagueViewSettling(false), 280)
    return () => clearTimeout(id)
  }, [isLeagueView, leagueViewSettling])

  useEffect(() => {
    if (isLeagueView && leagueViewSettling && filteredResults.length > 0) {
      setLeagueViewSettling(false)
    }
  }, [isLeagueView, leagueViewSettling, filteredResults.length])

  const { isLoadingWithDelay } = useSkeletonLoading({
    isLoading: effectiveLoading,
    isContentReady: effectiveResults.length > 0,
  })

  const handleRetryScoreboard = useCallback(() => {
    void refetchResults()
  }, [refetchResults])

  const showScoreboardLoadError =
    isError && !effectiveLoading && !isLoadingWithDelay && effectiveResults.length === 0

  return (
    <ErrorBoundary>
      <View style={styles.container}>
        {isFetchingWithData && <View style={styles.loadingBarTop} />}
        <PageHeader
          flavor={HEADER_FLAVORS.GAME_RESULTS}
          liveFilterOn={sectionFilter === SECTION_FILTER_KEYS.LIVE}
          onLiveFilterChange={(on) => {
            setSectionFilter(on ? SECTION_FILTER_KEYS.LIVE : SECTION_FILTER_KEYS.ALL)
            if (on) setSelectedDate(getTodayYYYYMMDD())
          }}
        />
        <View style={styles.headerSpacer} />
        <GuestBanner />

        <View style={styles.viewModeSwitcherRow}>
          <AnimatedSegmentedControl
            variant="compact"
            options={[...SCOREBOARD_VIEW_MODE_OPTIONS]}
            value={viewMode}
            onChange={(key) => handleViewModePress(key)}
            testID="game-results-view"
          />
        </View>

        <View
          style={[
            styles.pickerContainer,
            viewMode === SCOREBOARD_VIEW_MODE_KEYS.LEAGUE &&
              styles.pickerContainerLeague,
          ]}
        >
          {viewMode === SCOREBOARD_VIEW_MODE_KEYS.DATE ? (
            <>
              <Text style={styles.calendarMonthLabel}>{monthLabel}</Text>
              <CalendarStrip
                selectedDate={selectedDateAsDate}
                onDateSelect={handleDateSelect}
                showMonthHeader={false}
                testID="game-results-calendar"
              />
            </>
          ) : (
            <LeagueSelector
              availableLeagues={availableLeagues}
              selectedLeague={selectedLeague}
              onLeagueChange={setSelectedLeague}
            />
          )}
        </View>

        {/* Section filter pills (All, Recent Results, Upcoming) hidden; state/logic kept for Live toggle and filtering */}
        <View style={styles.sectionFilterRowHidden} pointerEvents="none">
          <View style={styles.sectionFilterChipsScroll}>
            <View style={styles.sectionFilterChipsScrollContent}>
              {SECTION_FILTER_OPTIONS.filter((opt) => opt.key !== SECTION_FILTER_KEYS.LIVE).map((opt) => {
                const isActive = sectionFilter === opt.key
                return (
                  <Pressable
                    key={opt.key}
                    style={({ pressed, hovered }) => [
                      styles.sectionFilterChip,
                      isActive && styles.sectionFilterChipActive,
                      Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                      pressed && styles.sectionFilterChipPressed,
                      hovered && Platform.OS === 'web' && styles.sectionFilterChipHover,
                    ]}
                    onPress={() => handleSectionFilterPress(opt.key)}
                  >
                    <Text
                      style={[
                        styles.sectionFilterChipText,
                        isActive && styles.sectionFilterChipTextActive,
                      ]}
                    >
                      {opt.label}
                    </Text>
                  </Pressable>
                )
              })}
            </View>
          </View>
        </View>

        {viewMode === SCOREBOARD_VIEW_MODE_KEYS.LEAGUE ? (
          <View
            style={[styles.gamesList, styles.gamesListLeague]}
            pointerEvents="box-none"
          >
            {showScoreboardLoadError ? (
              <DataLoadErrorPanel
                title={SCOREBOARD_ERROR_COPY.TITLE}
                message={SCOREBOARD_ERROR_COPY.MESSAGE}
                onRetry={handleRetryScoreboard}
                testID="scoreboard-load-error"
              />
            ) : (
              <GamesList
                filteredResults={effectiveResults}
                isLoading={isLoadingWithDelay}
                sectionFilter={sectionFilter}
                selectedDate={selectedDate}
                dayBucketIanaTimeZone={scoreboardIanaTimeZone}
                viewByLeague
              />
            )}
          </View>
        ) : (
          <View style={styles.gamesList}>
            {showScoreboardLoadError ? (
              <DataLoadErrorPanel
                title={SCOREBOARD_ERROR_COPY.TITLE}
                message={SCOREBOARD_ERROR_COPY.MESSAGE}
                onRetry={handleRetryScoreboard}
                testID="scoreboard-load-error"
              />
            ) : (
              <GamesList
                filteredResults={filteredResults}
                isLoading={isLoadingWithDelay}
                sectionFilter={sectionFilter}
                selectedDate={selectedDate}
                dayBucketIanaTimeZone={scoreboardIanaTimeZone}
                viewByLeague={false}
              />
            )}
          </View>
        )}
      </View>
    </ErrorBoundary>
  )
}
